import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { broadcastToMyGroups } from "@/lib/api/teamleader";
import { bidiText } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, GraduationCap, Send, Award, TrendingUp } from "lucide-react";

export function DashboardHeader() {
  const { user } = useAuth();
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState("");

  const broadcastMutation = useMutation({
    mutationFn: (content: string) => broadcastToMyGroups(content),
    onSuccess: () => {
      toast.success("Broadcast sent to all groups!");
      setBroadcastOpen(false);
      setBroadcastText("");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to send broadcast");
    },
  });

  const displayName = user?.username || (user?.email ? user.email.split("@")[0] : "");

  const dateFormatted = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Welcome back{displayName ? `, ${displayName}` : ""}!
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Your workspace at a glance · {dateFormatted}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/team-leader/training">
            <BookOpen className="h-4 w-4" aria-hidden="true" />
            <span>Assign Track</span>
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/team-leader/training">
            <GraduationCap className="h-4 w-4" aria-hidden="true" />
            <span>Assign Course</span>
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setBroadcastOpen(true)}
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          <span>Send Broadcast</span>
        </Button>
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/team-leader/certificates">
            <Award className="h-4 w-4" aria-hidden="true" />
            <span>Issue Certificate</span>
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/team-leader/analytics">
            <TrendingUp className="h-4 w-4" aria-hidden="true" />
            <span>View Analytics</span>
          </Link>
        </Button>
      </div>

      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Broadcast to All Groups</DialogTitle>
            <DialogDescription>
              Send an announcement message to all students in your assigned groups.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              className="min-h-[120px] resize-none"
              placeholder="Type your message to all groups..."
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              {...bidiText}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setBroadcastOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!broadcastText.trim() || broadcastMutation.isPending}
              onClick={() => broadcastMutation.mutate(broadcastText.trim())}
            >
              {broadcastMutation.isPending ? "Sending..." : "Send Broadcast"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
